import { supabase } from '@/lib/supabase';

/**
 * Users Service - Handles user profile operations with Supabase
 */

export const usersService = {
  /**
   * Get current user's profile
   * @returns {Promise<Object>}
   */
  async getCurrentUser() {
    // Use getSession instead of getUser to avoid 403 errors on fresh signups
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.user) return null;

    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', session.user.id)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  },

  /**
   * Update current user's profile
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>}
   */
  async updateMe(updates) {
    // Try to get user from session first (more reliable for fresh signups)
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    console.log('🔍 updateMe - session check:', { 
      hasSession: !!session, 
      hasUser: !!session?.user,
      userId: session?.user?.id,
      sessionError 
    });
    
    if (!session?.user) {
      console.error('❌ No session found in updateMe');
      throw new Error('Not authenticated');
    }

    const { data, error } = await supabase
      .from('users')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', session.user.id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Get user by ID
   * @param {string} id - User ID
   * @returns {Promise<Object>}
   */
  async getById(id) {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Get user by email
   * @param {string} email - User email
   * @returns {Promise<Object>}
   */
  async getByEmail(email) {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('email', email)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  },

  /**
   * Delete current user's account (POPIA-compliant soft delete)
   * - Soft deletes the user (sets deleted_at timestamp)
   * - Anonymizes personal data (email, name, phone, etc.)
   * - Preserves operational records (reports, tasks, etc.)
   * - Blocks deletion if user is the only estate manager
   * @returns {Promise<Object>} Result object with success status and message
   */
  async deleteAccount() {
    // Get current session
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.user) {
      throw new Error('Not authenticated');
    }

    // Call the database function to handle the deletion
    const { data, error } = await supabase.rpc('delete_user_account', {
      user_id: session.user.id
    });

    if (error) {
      console.error('❌ Delete account error:', error);
      throw error;
    }

    // Check if deletion was successful
    if (!data.success) {
      throw new Error(data.error || 'Failed to delete account');
    }

    // Sign out the user after successful deletion
    await supabase.auth.signOut();

    return data;
  }
};
